/**
 * Left-edge navigation drawer for phones.
 *
 * The desktop sidebar is hidden below md, which left mobile users with
 * no path to Jobs / Clients / Contractors / Accounting at all. The
 * hamburger in the mobile top bar opens this drawer: the SAME nav list
 * the sidebar renders (shared MainNavItems, so the two can never
 * drift), with the identity block + sign out at the bottom.
 *
 * Chrome mirrors RightDrawer (backdrop, escape, slide animation) but
 * from the left edge, and closes itself on any nav tap.
 */
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { classNames } from '@/lib/format';
import { MainNavItems } from './Sidebar';
import { Monomark } from './Logo';
import { IconClose, IconSignOut } from './Icons';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function MobileNavDrawer({ open, onClose }: Props) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Escape closes, like every other drawer in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open so the page doesn't drift under a
  // swiping thumb.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={classNames(
          'fixed inset-0 bg-kg-black/30 transition-opacity z-40 md:hidden',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      <aside
        role="dialog"
        aria-label="Navigation"
        aria-hidden={!open}
        className={classNames(
          'fixed left-0 top-0 h-full w-[19rem] max-w-[85vw] md:hidden',
          // Apple glass, matching RightDrawer: this floats over the page,
          // so it gets the blurred material rather than an opaque slab
          // with a hairline down its right edge. Only the right corners
          // round; the left three sit against the window edge.
          'kg-pane rounded-none rounded-r-2xl',
          'z-50 transition-transform duration-300 ease-kg-out flex flex-col',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <header className="px-5 h-16 flex items-center justify-between shrink-0 bg-white/40 dark:bg-white/[0.05]">
          <Link to="/" onClick={onClose} aria-label="Kilogram dashboard">
            <Monomark variant="black" className="h-7 w-auto dark:hidden" />
            <Monomark variant="white" className="h-7 w-auto hidden dark:inline-block" />
          </Link>
          <button
            onClick={onClose}
            aria-label="Close navigation"
            className="text-kg-grey-700 dark:text-kg-grey-300 hover:text-kg-black dark:hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black p-1"
          >
            <IconClose className="w-5 h-5" />
          </button>
        </header>

        <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <MainNavItems role={user?.role} isOwner={user?.isOwner} onNavigate={onClose} />
        </nav>

        <div className="px-6 py-4 shrink-0 bg-white/40 dark:bg-white/[0.05]">
          {user && (
            <div className="mb-3">
              <p className="text-sm font-medium text-kg-black dark:text-white truncate">
                {user.name}
              </p>
              <p
                className={
                  'text-xs uppercase tracking-widest mt-0.5 ' +
                  (user.role === 'EDITOR'
                    ? 'text-sky-600 dark:text-sky-400 font-semibold'
                    : 'text-kg-grey-500 dark:text-kg-grey-400')
                }
              >
                {user.role === 'EDITOR' ? 'Editor (restricted)' : user.role.toLowerCase()}
              </p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-kg-grey-700 hover:text-kg-black dark:text-kg-grey-300 dark:hover:text-white transition-colors py-1"
          >
            <IconSignOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
